import React from 'react';
import JSZip from 'jszip';
import { logError } from '@utils/usefulFuncs';
import { getItemFromStorage, setItemToStorage } from '@utils/storage';
import { downloadUserDoc } from '@services/docs';
import {
  DEFAULT_NAMING_TEMPLATE,
  DOCUMENT_DOWNLOAD_KINDS,
  applyNamingTemplate,
  buildZipFilename,
  dedupeZipEntryNames,
  isPdfDocument,
  saveBlobAsFile,
} from '@utils/documentVault';

export const QUEUE_ITEM_STATUS = {
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
  CANCELED: 'canceled',
};

// Enough files that a mis-click is expensive but a real bulk pull still goes through unprompted.
export const BULK_ORIGINALS_WARN_THRESHOLD = 25;

/*
  Clean copies are capped hard. Each one re-runs the whole watermark-stripping pipeline server
  side behind a 180s read timeout with nothing cached, so ten files is already a worst case of
  half an hour. A larger request is almost certainly a mis-click.
*/
export const CLEAN_COPY_MAX_SELECTION = 10;

const NAMING_TEMPLATE_STORAGE_KEY = 'documentVaultNamingTemplate';

const defaultState = {
  items: [],
  isProcessing: false,
  kind: null,
  summary: null,
  history: [],
  namingTemplate: DEFAULT_NAMING_TEMPLATE,
  startOriginalsZip: () => logError('startOriginalsZip called without a DownloadQueueProvider'),
  startCleanCopies: () => logError('startCleanCopies called without a DownloadQueueProvider'),
  retryFailed: () => logError('retryFailed called without a DownloadQueueProvider'),
  cancel: () => logError('cancel called without a DownloadQueueProvider'),
  dismiss: () => logError('dismiss called without a DownloadQueueProvider'),
  setNamingTemplate: () => logError('setNamingTemplate called without a DownloadQueueProvider'),
};

export const DownloadQueueContext = React.createContext(defaultState);

const ACTIONS = {
  START: 'START',
  ITEM_STATUS: 'ITEM_STATUS',
  FINISH: 'FINISH',
  CANCEL: 'CANCEL',
  DISMISS: 'DISMISS',
  RECORD_HISTORY: 'RECORD_HISTORY',
};

const initialState = {
  items: [],
  isProcessing: false,
  kind: null,
  summary: null,
  history: [],
};

export function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.START: {
      const { docs, kind } = action.payload;
      return {
        ...state,
        items: docs.map((doc) => ({
          id: doc.id,
          blobId: doc.blob_id,
          filename: doc.filename,
          kind,
          status: QUEUE_ITEM_STATUS.QUEUED,
          error: null,
        })),
        isProcessing: true,
        kind,
        summary: null,
      };
    }

    case ACTIONS.ITEM_STATUS: {
      const { id, status, error } = action.payload;
      return {
        ...state,
        items: state.items.map((item) => (item.id === id ? { ...item, status, error: error || null } : item)),
      };
    }

    case ACTIONS.FINISH: {
      return { ...state, isProcessing: false, summary: action.payload };
    }

    case ACTIONS.CANCEL: {
      return {
        ...state,
        isProcessing: false,
        items: state.items.map((item) =>
          item.status === QUEUE_ITEM_STATUS.QUEUED || item.status === QUEUE_ITEM_STATUS.RUNNING
            ? { ...item, status: QUEUE_ITEM_STATUS.CANCELED }
            : item
        ),
        summary: action.payload,
      };
    }

    case ACTIONS.RECORD_HISTORY: {
      // Session-scoped only: there is no backend audit trail in single-user scope.
      return { ...state, history: [action.payload, ...state.history] };
    }

    case ACTIONS.DISMISS: {
      return { ...initialState, history: state.history };
    }

    default:
      throw new Error(`missing case: DownloadQueueContext (${action.type})`);
  }
}

export const DownloadQueueProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [namingTemplate, setNamingTemplateState] = React.useState(
    () => getItemFromStorage(NAMING_TEMPLATE_STORAGE_KEY) || DEFAULT_NAMING_TEMPLATE
  );
  const abortRef = React.useRef(null);
  const isCanceledRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  const setNamingTemplate = React.useCallback((template) => {
    setNamingTemplateState(template);
    setItemToStorage(NAMING_TEMPLATE_STORAGE_KEY, template);
  }, []);

  /*
    The backend hands back no job id for a clean copy, so an in-flight pass cannot be reattached
    to after a reload -- warn before the tab goes away rather than losing minutes of work silently.
  */
  React.useEffect(() => {
    if (!state.isProcessing) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.isProcessing]);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const cancel = React.useCallback(() => {
    isCanceledRef.current = true;
    abortRef.current?.abort();
    dispatch({ type: ACTIONS.CANCEL, payload: { canceled: true } });
  }, []);

  const dismiss = React.useCallback(() => {
    dispatch({ type: ACTIONS.DISMISS });
  }, []);

  /*
    Originals are fetched one at a time and packed into a single ZIP.

    Sequential rather than parallel to keep the request pattern gentle, and a ZIP rather than N
    anchor clicks because browsers throttle rapid programmatic downloads past a handful of files.
  */
  const startOriginalsZip = React.useCallback(async (docs) => {
    if (!docs?.length) return null;

    isCanceledRef.current = false;
    const controller = new AbortController();
    abortRef.current = controller;
    dispatch({ type: ACTIONS.START, payload: { docs, kind: DOCUMENT_DOWNLOAD_KINDS.original } });

    const zip = new JSZip();
    const usedNames = [];
    const succeeded = [];
    const failed = [];

    for (const doc of docs) {
      if (isCanceledRef.current) break;

      dispatch({ type: ACTIONS.ITEM_STATUS, payload: { id: doc.id, status: QUEUE_ITEM_STATUS.RUNNING } });

      const result = await downloadUserDoc(doc.blob_id, {
        filename: doc.filename,
        signal: controller.signal,
      });

      // An aborted request has no bytes to keep. A request that finished before the cancel
      // registered does -- discarding it would throw away a file the user already waited for.
      if (result.canceled) break;

      if (result.ok) {
        const entryName = dedupeZipEntryNames(result.filename || doc.filename, usedNames);
        usedNames.push(entryName);
        zip.file(entryName, result.blob);
        succeeded.push(doc);
        dispatch({ type: ACTIONS.ITEM_STATUS, payload: { id: doc.id, status: QUEUE_ITEM_STATUS.COMPLETED } });
      } else {
        failed.push({ doc, error: result.error });
        dispatch({
          type: ACTIONS.ITEM_STATUS,
          payload: { id: doc.id, status: QUEUE_ITEM_STATUS.ERROR, error: result.error },
        });
      }

      if (isCanceledRef.current) break;
    }

    abortRef.current = null;

    if (isCanceledRef.current) {
      // Whatever finished before the cancel still ships -- discarding it would waste work the
      // user already waited for.
      if (succeeded.length > 0) {
        const blob = await zip.generateAsync({ type: 'blob' });
        saveBlobAsFile(blob, buildZipFilename(succeeded.length));
      }
      return { canceled: true, succeeded: succeeded.length, failed: failed.length };
    }

    if (succeeded.length > 0) {
      const blob = await zip.generateAsync({ type: 'blob' });
      saveBlobAsFile(blob, buildZipFilename(succeeded.length));
    }

    const summary = {
      total: docs.length,
      succeeded: succeeded.length,
      failed: failed.map((f) => ({ id: f.doc.id, filename: f.doc.filename, error: f.error })),
    };

    if (isMountedRef.current) {
      dispatch({ type: ACTIONS.FINISH, payload: summary });
    }
    return summary;
  }, []);

  /*
    Clean copies are never zipped and never parallel.

    Each call re-runs the full cleaning pipeline behind a 180s server-side timeout, so holding
    finished files in memory while later ones cook would risk a large heap and lose everything on
    a cancel. Saving each file the moment it lands means a canceled run still keeps its work.
  */
  const startCleanCopies = React.useCallback(
    async (docs) => {
      const eligible = (docs || []).filter(isPdfDocument).slice(0, CLEAN_COPY_MAX_SELECTION);
      if (eligible.length === 0) return null;

      isCanceledRef.current = false;
      const controller = new AbortController();
      abortRef.current = controller;
      dispatch({ type: ACTIONS.START, payload: { docs: eligible, kind: DOCUMENT_DOWNLOAD_KINDS.clean } });

      const succeeded = [];
      const failed = [];

      for (const doc of eligible) {
        if (isCanceledRef.current) break;

        dispatch({ type: ACTIONS.ITEM_STATUS, payload: { id: doc.id, status: QUEUE_ITEM_STATUS.RUNNING } });

        const result = await downloadUserDoc(doc.blob_id, {
          clean: true,
          filename: doc.filename,
          signal: controller.signal,
        });

        if (result.canceled) break;

        if (result.ok) {
          saveBlobAsFile(result.blob, applyNamingTemplate(doc, namingTemplate, { clean: true }));
          succeeded.push(doc);
          dispatch({
            type: ACTIONS.ITEM_STATUS,
            payload: { id: doc.id, status: QUEUE_ITEM_STATUS.COMPLETED },
          });
          dispatch({
            type: ACTIONS.RECORD_HISTORY,
            payload: {
              id: `${doc.id}-${succeeded.length}-${failed.length}`,
              docId: doc.id,
              filename: doc.filename,
              kind: DOCUMENT_DOWNLOAD_KINDS.clean,
              status: QUEUE_ITEM_STATUS.COMPLETED,
              // A 200 from a non-PDF is a passthrough; eligibility already excluded those, but
              // the flag is what the endpoint actually reports.
              wasCleaned: !!result.wasCleaned,
            },
          });
        } else {
          failed.push({ doc, error: result.error });
          dispatch({
            type: ACTIONS.ITEM_STATUS,
            payload: { id: doc.id, status: QUEUE_ITEM_STATUS.ERROR, error: result.error },
          });
          dispatch({
            type: ACTIONS.RECORD_HISTORY,
            payload: {
              id: `${doc.id}-${succeeded.length}-${failed.length}`,
              docId: doc.id,
              filename: doc.filename,
              kind: DOCUMENT_DOWNLOAD_KINDS.clean,
              status: QUEUE_ITEM_STATUS.ERROR,
              error: result.error,
            },
          });
        }

        if (isCanceledRef.current) break;
      }

      abortRef.current = null;

      const summary = {
        total: eligible.length,
        succeeded: succeeded.length,
        failed: failed.map((f) => ({ id: f.doc.id, filename: f.doc.filename, error: f.error })),
        canceled: isCanceledRef.current,
      };

      if (isMountedRef.current && !isCanceledRef.current) {
        dispatch({ type: ACTIONS.FINISH, payload: summary });
      }
      return summary;
    },
    [namingTemplate]
  );

  const retryFailed = React.useCallback(
    (docsById) => {
      const failedDocs = (state.summary?.failed || []).map((f) => docsById[f.id]).filter(Boolean);
      if (failedDocs.length === 0) return null;
      return startOriginalsZip(failedDocs);
    },
    [state.summary, startOriginalsZip]
  );

  const value = React.useMemo(
    () => ({
      items: state.items,
      isProcessing: state.isProcessing,
      kind: state.kind,
      summary: state.summary,
      history: state.history,
      namingTemplate,
      startOriginalsZip,
      startCleanCopies,
      retryFailed,
      cancel,
      dismiss,
      setNamingTemplate,
    }),
    [
      state.items,
      state.isProcessing,
      state.kind,
      state.summary,
      state.history,
      namingTemplate,
      startOriginalsZip,
      startCleanCopies,
      retryFailed,
      cancel,
      dismiss,
      setNamingTemplate,
    ]
  );

  return <DownloadQueueContext.Provider value={value}>{children}</DownloadQueueContext.Provider>;
};
